import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import CardNB from '../../../components/ui-nb/CardNB';
import BadgeNB from '../../../components/ui-nb/BadgeNB';
import { useTranslation } from '../../../i18n';

interface Technician {
  id: string;
  full_name: string;
  role: string;
  active_orders: number;
}

export default function TechnicianList() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const organizationId = useAuthStore(s => s.user?.organizationId);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) return;
    async function fetchTechnicians() {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, role')
        .eq('organization_id', organizationId)
        .in('role', ['technician', 'admin']);

      // Get active orders count per technician
      const { data: orders } = await supabase
        .from('orders')
        .select('assigned_to')
        .eq('branches.organization_id', organizationId)
        .is('completed_at', null)
        .not('assigned_to', 'is', null);

      const orderCounts: Record<string, number> = {};
      (orders || []).forEach(o => {
        orderCounts[o.assigned_to] = (orderCounts[o.assigned_to] || 0) + 1;
      });

      setTechnicians((data || []).map(p => ({
        ...p,
        active_orders: orderCounts[p.id] || 0,
      })));
      setLoading(false);
    }
    fetchTechnicians();
  }, [organizationId]);

  return (
    <>
      <h1 className="text-2xl font-bold text-black font-nb mb-6">{t('technicians.title') || 'Technicians'}</h1>

      {loading ? (
        <p className="text-nb-text-muted">Loading...</p>
      ) : technicians.length === 0 ? (
        <CardNB className="text-center" style={{ padding: '2rem', textAlign: 'center' }}>
          <p className="text-nb-text-muted">No technicians yet</p>
        </CardNB>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {technicians.map(tech => (
            <CardNB key={tech.id} variant="interactive" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '3rem', height: '3rem', borderRadius: '0',
                  background: '#000000', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 700, fontSize: '1.125rem', border: '2px solid #000',
                }}>
                  {tech.full_name?.charAt(0) || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: '#000' }}>{tech.full_name || '—'}</p>
                  <p style={{ fontSize: '0.875rem', color: '#787878' }}>{tech.role}</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#000000' }}>{tech.active_orders}</p>
                  <p style={{ fontSize: '0.75rem', color: '#787878' }}>active</p>
                </div>
              </div>
            </CardNB>
          ))}
        </div>
      )}
    </>
  );
}
